import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import type { ListRegulation, V1OrganizationSignUpRequest } from '@zelos/codegen/apis';
import { V1Service } from '@zelos/codegen/apis';
import { regex } from '@zelos/shared/constants/regex';
import { setReactHookFormErrorByErrors, throwTypedApiError, toTypedApiError } from '@zelos/shared/lib/typed-api-error';
import produce from 'immer';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { InputAboveDiv, InputWrapDiv, InputWrapSpan } from '../SignInput';
import Regulation from '../tab/up/Regulation';
import RegulationModal from '../tab/up/RegulationModal';
import type { AddressInfoType } from '@/components/comm/AddressModal';
import AddressModal from '@/components/comm/AddressModal';
import Button from '@/components/comm/Button';
import Checkbox from '@/components/comm/Checkbox';
import Divider from '@/components/comm/Divider';
import HelpTooltip from '@/components/comm/HelpTooltip';
import ImageButton from '@/components/comm/ImageButton';
import Input from '@/components/comm/Input';
import Margin from '@/components/comm/Margin';
import RequiredAsterisk from '@/components/comm/RequiredAsterisk';
import SingleFileInput from '@/components/comm/SingleFileInput';
import { breakpointSmall } from '@/constants/breakpoints';
import { colors } from '@/constants/colors';
import { createErrorToast, showToast } from '@/lib/toast';
import icEyeSlash from '@/public/images/ic-eye-slash.svg';
import icEye from '@/public/images/ic-eye.svg';
import searchIcon from '@/public/images/ic-search.svg';

interface OrganizationSignUpType extends Omit<V1OrganizationSignUpRequest, 'marketingAgreement'> {
  passwordCheck: string;
}

interface CheckRegulation extends ListRegulation {
  checked: boolean;
}

const FormStyled = styled.form`
display: flex;
max-height: 963px;
flex-direction: column;
background-color: ${colors.white};
border-radius: 15px;
@media (max-width: ${breakpointSmall}) {
  max-height: 80vh;
}
`;

const TitleBox = styled.div`
display: grid;
width: 100%;
flex-shrink: 0;
align-content: flex-start;
padding: 40px 0px 8px 0px;
place-items: center;
& > span {
  color: ${colors.textPrimary};
  font-size: 18px;
  font-weight: 700;
  @media (max-width: ${breakpointSmall}) {
    font-size: 16px;
  }
}
`;

const ContentContainer = styled.div`
display: flex;
max-height: 963px;
flex-direction: column;
align-items: center;
padding: 0 32px 32px 32px;
overflow-y: auto;
scrollbar-width: none;
::-webkit-scrollbar {
  display: none;
}
::-webkit-scrollbar-thumb {
  border: 7px solid transparent;
  background-clip: padding-box;
  background-color: ${colors.lightGrey3};
  border-radius: 100px;
}
@media (max-width: ${breakpointSmall}) {
  padding: 0 20px 20px 20px;
}
`;

const CheckBoxWrapDiv = styled.div`
display: grid;
width: 100%;
margin-top: 55px;
row-gap: 13px;
`;

const BottomBox = styled.div`
width: 100%;
padding: 20px 32px 56px 32px;
@media (max-width: ${breakpointSmall}) {
  padding: 20px 20px 40px 20px;
}
`;

const SignUpButton = styled(Button)`
width: 100%;
height: 54px;
`;

const TooltipText = styled.span`
color: ${colors.white};
font-size: 12px;
`;

const shadowSize = '20px';

const TopShadow = styled.p<{ visible: boolean }>`
position: relative;
height: 0;
visibility: ${({ visible }) => (visible ? '' : 'hidden')};
& > span {
  position: absolute;
  z-index: 1;
  width: 100%;
  padding-bottom: ${shadowSize};
  background: linear-gradient(180deg, rgba(0,0,0,0.07) 0%, transparent 100%);
  @media (max-width: ${breakpointSmall}) {
    background: linear-gradient(180deg, rgba(0,0,0,0.05) 0%, transparent 100%);
  }
}
`;

const BottomShadow = styled.p<{ visible: boolean }>`
position: relative;
height: 0;
visibility: ${({ visible }) => (visible ? '' : 'hidden')};
& > span {
  position: absolute;
  top: -${shadowSize};
  width: 100%;
  padding-top: ${shadowSize};
  background: linear-gradient(0deg, rgba(0,0,0,0.07) 0%, transparent 100%);
  @media (max-width: ${breakpointSmall}) {
    background: linear-gradient(0deg, rgba(0,0,0,0.05) 0%, transparent 100%);
  }
}
`;

const OrganizationSignUp = () => {
  const router = useRouter();
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [checkRegulationsList, setCheckRegulationsList] = useState<CheckRegulation[]>([]);

  const topObserveTarget = useRef<HTMLParagraphElement | null>(null);
  const bottomObserveTarget = useRef<HTMLParagraphElement | null>(null);
  const scrollBottomRef = useRef(false);
  const scrollTopRef = useRef(false);
  const [scrollBottom, setScrollBottom] = useState(false);
  const [scrollTop, setScrollTop] = useState(false);

  const { data: regulationsList } = useQuery(
    ['v1RegulationsList', { scope: 'ORR' }],
    async () => {
      const regulationsList = await V1Service.v1RegulationsList({ scope: 'ORR' });
      regulationsList.sort(
        (a, b) => -(Number(a.isRequired || false) - Number(b.isRequired || false)) || a.id - b.id,
      );

      return regulationsList;
    },
    { refetchOnWindowFocus: false },
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<OrganizationSignUpType>();
  const watchPassword = watch('password');
  const watchEmail = watch('email');

  const handleAddressResult = useCallback((addressInfo: AddressInfoType) => {
    setValue('address', addressInfo.address);
    setValue('latitude', addressInfo.latitude);
    setValue('longitude', addressInfo.longtitude);
  }, [setValue]);

  const signUpSubmit = useCallback(async ({
    name,
    phone,
    department,
    address,
    detailAddress,
    region,
    longitude,
    latitude,
    businessLicense,
    password,
    email,
    userName,
    emailVerificationDomain,
  }: OrganizationSignUpType) => {
    if (checkRegulationsList.some(({ isRequired, checked }) => isRequired && !checked)) {
      showToast({ text: '필수 항목을 체크해 주세요' });
      return;
    }

    try {
      const marketingAgreement = checkRegulationsList.find(
        ({ name }) => /marketing|마케팅/i.test(name),
      )?.checked || false;

      await V1Service.v1UsersOrganizationSignUpCreate({
        formData: {
          name,
          phone,
          department,
          address,
          detailAddress,
          region,
          longitude,
          latitude,
          businessLicense,
          password,
          email,
          userName,
          marketingAgreement,
          emailVerificationDomain,
        },
      }).catch(throwTypedApiError);

      showToast({ text: '단체 회원가입이 접수되었습니다.' });
      await V1Service.v1LogsActionsTrackingsCreate({
        requestBody: {
          url: window.location.href,
          actionTrackingEvent: 30,
        },
      });
      router.replace('/');
    } catch (error) {
      const errorBody = toTypedApiError(error);
      if (errorBody) {
        setReactHookFormErrorByErrors(setError, errorBody.errors);
        createErrorToast(errorBody);
      } else {
        throw error;
      }
    }
  }, [checkRegulationsList, router, setError]);

  useEffect(() => {
    if (!regulationsList) return;

    setCheckRegulationsList(
      regulationsList.map((checkRegulation) =>
        ({ ...checkRegulation, checked: false })),
    );
  }, [regulationsList]);

  useEffect(() => {
    watchEmail
    && regex.email.test(watchEmail)
    && setValue('emailVerificationDomain', watchEmail.split('@')[1]);
  }, [setValue, watchEmail]);

  useEffect(() => {
    if (topObserveTarget.current && bottomObserveTarget.current) {
      const topObserver = new IntersectionObserver((entries) => {
        if (entries[0].intersectionRatio === 1) {
          if (!scrollTopRef.current) setScrollTop(true);
          scrollTopRef.current = true;
        } else {
          setScrollTop(false);
          scrollTopRef.current = false;
        }
      }, { threshold: 1 });
      topObserver.observe(topObserveTarget.current);

      const bottomObserver = new IntersectionObserver((entries) => {
        if (entries[0].intersectionRatio === 1) {
          if (!scrollBottomRef.current) setScrollBottom(true);
          scrollBottomRef.current = true;
        } else {
          setScrollBottom(false);
          scrollBottomRef.current = false;
        }
      });
      bottomObserver.observe(bottomObserveTarget.current);
      return () => {
        topObserver.disconnect();
        bottomObserver.disconnect();
      };
    }
  }, []);

  return (
    <FormStyled onSubmit={handleSubmit(signUpSubmit)} noValidate>
      <TitleBox>
        <span>단체 회원가입</span>
      </TitleBox>
      <TopShadow visible={!scrollTop}><span /></TopShadow>
      <ContentContainer>
        <p ref={topObserveTarget} />
        <InputWrapDiv>
          <InputAboveDiv>
            <InputWrapSpan>
              <span>단체명</span>
              <RequiredAsterisk />
            </InputWrapSpan>
          </InputAboveDiv>
          <Input
            type="text"
            placeholder="단체명을 입력해 주세요"
            error={errors.name}
            {...register('name', {
              required: '단체명을 입력해 주세요',
            })}
          />
        </InputWrapDiv>
        <InputWrapDiv>
          <InputAboveDiv>
            <InputWrapSpan>
              <span>단체 이메일</span>
              <RequiredAsterisk />
              <Margin />
              <HelpTooltip width="260px">
                <TooltipText>입력하신 이메일로 로그인하실 수 있습니다.</TooltipText>
              </HelpTooltip>
            </InputWrapSpan>
          </InputAboveDiv>
          <Input
            type="email"
            placeholder="이메일을 입력해 주세요"
            error={errors.email}
            {...register('email', {
              required: '이메일을 입력해 주세요',
              pattern: {
                value: regex.email,
                message: '올바르지 않은 이메일 형식입니다.',
              },
              validate: {
                async asyncValidate(value) {
                  try {
                    const { isDuplicate } = await V1Service.v1UsersEmailDuplicateCreate({ requestBody: { email: value } });
                    return !isDuplicate || '중복된 이메일입니다.';
                  } catch {
                    return '이메일 확인 중 오류가 발생했습니다.';
                  }
                },
              },
            })}
          />
        </InputWrapDiv>
        <InputWrapDiv>
          <InputAboveDiv>
            <InputWrapSpan>
              <span>이메일 도메인</span>
              <Margin />
              <HelpTooltip width="260px">
                <TooltipText>임직원 인증에 사용됩니다.</TooltipText>
              </HelpTooltip>
            </InputWrapSpan>
          </InputAboveDiv>
          <Input
            placeholder="이메일 도메인을 입력해 주세요"
            error={errors.emailVerificationDomain}
            {...register('emailVerificationDomain', {
              pattern: {
                value: regex.emailDomain,
                message: '올바르지 않은 이메일 도메인 형식입니다.',
              },
            })}
          />
        </InputWrapDiv>
        <InputWrapDiv>
          <InputAboveDiv>
            <InputWrapSpan>
              <span>비밀번호</span>
              <RequiredAsterisk />
            </InputWrapSpan>
          </InputAboveDiv>
          <Input
            type={showPassword ? 'text' : 'password'}
            placeholder="(문자, 숫자, 특수문자 포함 최소 8자)"
            autoComplete={showPassword ? 'off' : 'on'}
            error={errors.password}
            {...register('password', {
              required: '비밀번호를 입력해 주세요',
              pattern: {
                value: regex.password,
                message: '문자, 숫자, 특수문자 포함 최소 8자를 입력해 주세요',
              },
            })}
            end={(
              <ImageButton
                onClick={() => setShowPassword(!showPassword)}
                src={showPassword ? icEye : icEyeSlash}
                alt="show password"
                width={24}
                height={17}
                style={{
                  objectFit: 'contain',
                }}
              />
            )}
          />
          <Input
            type={showPasswordConfirm ? 'text' : 'password'}
            placeholder="비밀번호를 확인해 주세요"
            autoComplete={showPasswordConfirm ? 'off' : 'on'}
            error={errors.passwordCheck}
            {...register('passwordCheck', {
              required: '비밀번호를 확인해 주세요',
              pattern: {
                value: regex.password,
                message: '문자, 숫자, 특수문자 포함 최소 8자를 입력해 주세요',
              },
              validate: {
                positive: (value) => value === watchPassword || '비밀번호가 일치하지 않습니다.',
              },
            })}
            end={(
              <ImageButton
                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                src={showPasswordConfirm ? icEye : icEyeSlash}
                alt="show password"
                width={24}
                height={17}
                style={{
                  objectFit: 'contain',
                }}
              />
            )}
          />
        </InputWrapDiv>
        <InputWrapDiv>
          <InputAboveDiv>
            <InputWrapSpan>
              <span>담당자 정보</span>
              <RequiredAsterisk />
            </InputWrapSpan>
          </InputAboveDiv>
          <Input
            type="text"
            placeholder="이름을 입력해 주세요"
            error={errors.userName}
            {...register('userName', {
              required: '이름을 입력해 주세요',
              pattern: {
                value: regex.name,
                message: '올바른 이름을 입력해 주세요.',
              },
            })}
          />
          <Input
            type="text"
            placeholder="담당 부서명을 입력해 주세요"
            error={errors.department}
            {...register('department', {
              required: '담당 부서명을 입력해 주세요',
            })}
          />
          <Input
            type="text"
            placeholder="담당자 연락처를 입력해 주세요"
            error={errors.phone}
            {...register('phone', {
              required: '담당자 연락처를 입력해 주세요',
              pattern: {
                value: regex.tel,
                message: '올바른 연락처를 입력해 주세요.',
              },
            })}
          />
        </InputWrapDiv>
        <InputWrapDiv>
          <InputAboveDiv>
            <InputWrapSpan>
              <span>주소</span>
              <RequiredAsterisk />
            </InputWrapSpan>
          </InputAboveDiv>
          <Input
            type="text"
            placeholder="도로명, 지번, 건물명으로 검색해 주세요"
            error={errors.address}
            {...register('address', {
              required: '주소를 입력해 주세요',
            })}
            end={(
              <ImageButton
                src={searchIcon}
                alt="search"
                onClick={() => setShowAddressModal(true)}
              />
            )}
            onClick={() => setShowAddressModal(true)}
            readOnly
          />
          <Input
            type="text"
            placeholder="상세 주소를 입력해 주세요"
            error={errors.detailAddress}
            {...register('detailAddress', {
              required: '상세 주소를 입력해 주세요',
            })}
          />
        </InputWrapDiv>
        <InputWrapDiv>
          <InputAboveDiv>
            <InputWrapSpan>
              <span>사업자 등록증</span>
              <RequiredAsterisk />
            </InputWrapSpan>
          </InputAboveDiv>
          <SingleFileInput
            error={errors.businessLicense?.message}
            accept=".pdf, image/*"
            {...register('businessLicense', {
              required: '파일을 첨부해주세요',
            })}
          />
        </InputWrapDiv>
        <CheckBoxWrapDiv>
          <Checkbox
            boxStyle={{
              fontSize: 14,
              fontWeight: 700,
            }}
            checked={checkRegulationsList.every(({ checked }) => checked)}
            onChange={() => {
              const isWillCheckAll = !checkRegulationsList.every(({ checked }) => checked);
              setCheckRegulationsList(
                checkRegulationsList.map((checkRegulation) =>
                  ({ ...checkRegulation, checked: isWillCheckAll })),
              );
            }}
          >전체 동의
          </Checkbox>
          <Divider color={colors.lightGrey2} />
          {checkRegulationsList.map((regulation, index) => (
            <Checkbox
              boxStyle={{
                color: colors.grey4,
                fontSize: 13,
                fontWeight: 400,
                lineHeight: 19,
              }}
              key={regulation.id}
              checked={regulation.checked}
              onChange={() => {
                setCheckRegulationsList(produce((draft) => {
                  draft[index].checked = !regulation.checked;
                }));
              }}
            >
              <Regulation regulation={regulation} />
            </Checkbox>
          ))}
          <RegulationModal />
        </CheckBoxWrapDiv>
        <p ref={bottomObserveTarget} />
      </ContentContainer>
      <BottomShadow visible={!scrollBottom}><span /></BottomShadow>
      <BottomBox>
        <SignUpButton type="submit">회원가입</SignUpButton>
      </BottomBox>
      <AddressModal
        showState={[showAddressModal, setShowAddressModal]}
        setResult={handleAddressResult}
      />
    </FormStyled>
  );
};

export default OrganizationSignUp;
